import { preprocessWidgetsForEditor } from './widget-snapshot.helper';
import { getWidgetDisplayLabel } from '../widget-registry';
import { WIDGET_SNAPSHOT_CLASS } from './widget-editor.constants';

type CK = typeof import('ckeditor5');

const PLACEHOLDER_REGEX = /\[\[([^\]]+)\]\]/g;
const BLOCK_MARKER_REGEX = /\[\[\s*(#each\s+[^\]]+|\/each|#if\s+[^\]]+|\/if)\s*\]\]/g;

export const LOOP_FRAME_CLASS = 'cke-loop-frame';
export const LOOP_FRAME_LABEL_CLASS = 'cke-loop-frame__label';
export const LOOP_FRAME_BODY_CLASS = 'cke-loop-frame__body';
export const COND_FRAME_CLASS = 'cke-cond-frame';
export const COND_FRAME_LABEL_CLASS = 'cke-cond-frame__label';
export const COND_FRAME_BODY_CLASS = 'cke-cond-frame__body';
export { WIDGET_SNAPSHOT_CLASS } from './widget-editor.constants';
const LOOP_REPEAT_ICON = '↺';

export interface FrameLabels {
    repeatsForEach: string;
    shownOnlyWhen: string;
}

const DEFAULT_FRAME_LABELS: FrameLabels = {
    repeatsForEach: 'Repeats for each',
    shownOnlyWhen: 'shown only when'
};

export enum LoopBoundaryKind {
    EachOpen = 'each-open',
    EachClose = 'each-close',
    IfOpen = 'if-open',
    IfClose = 'if-close'
}

interface LoopBoundary {
    kind: LoopBoundaryKind;
    expression: string;
    token: string;
}

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

// Inverse of encodeSnapshotHtml (widget-snapshot.helper): UTF-8 safe base64 → HTML string.
function decodeSnapshotHtml(b64: string): string {
    if (!b64) return '';
    try {
        const binary = atob(b64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return new TextDecoder().decode(bytes);
    } catch {
        return '';
    }
}

export function parseWidgetAttrs(raw: string): Record<string, string> {
    if (!raw) return {};
    try {
        const parsed = JSON.parse(decodeHtmlEntities(raw)) as unknown;
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
        const result: Record<string, string> = {};
        for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
            if (key === 'class') continue;
            result[key] = String(value);
        }
        return result;
    } catch {
        return {};
    }
}

function humanizeExpression(expression: string): string {
    let path = expression.trim();
    while (path.startsWith('../')) {
        path = path.slice('../'.length).trim();
    }
    if (path.startsWith('this.')) {
        path = path.slice('this.'.length);
    }
    const last = path.split('.').pop() || path;
    if (!last) return expression.trim();
    return last
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/^./, s => s.toUpperCase())
        .trim();
}

function buildChipSpan(path: string): string {
    const safe = escapeHtml(path);
    return `<span class="cke-placeholder-chip" data-path="${safe}" contenteditable="false">{{ ${safe} }}</span>`;
}

function parseLoopBoundary(directive: string): LoopBoundary {
    const trimmed = directive.trim();
    if (trimmed.startsWith('#each')) {
        return { kind: LoopBoundaryKind.EachOpen, expression: trimmed.slice('#each'.length).trim(), token: `[[${trimmed}]]` };
    }
    if (trimmed === '/each') {
        return { kind: LoopBoundaryKind.EachClose, expression: '', token: '[[/each]]' };
    }
    if (trimmed.startsWith('#if')) {
        return { kind: LoopBoundaryKind.IfOpen, expression: trimmed.slice('#if'.length).trim(), token: `[[${trimmed}]]` };
    }
    return { kind: LoopBoundaryKind.IfClose, expression: '', token: '[[/if]]' };
}

function loopBoundaryLabel(kind: LoopBoundaryKind, expression: string): string {
    switch (kind) {
        case LoopBoundaryKind.EachOpen:
            return `{{#each ${expression}}}`;
        case LoopBoundaryKind.EachClose:
            return '{{/each}}';
        case LoopBoundaryKind.IfOpen:
            return `{{#if ${expression}}}`;
        case LoopBoundaryKind.IfClose:
            return '{{/if}}';
    }
}

function loopBoundaryToken(kind: LoopBoundaryKind, expression: string): string {
    switch (kind) {
        case LoopBoundaryKind.EachOpen:
            return `[[#each ${expression}]]`;
        case LoopBoundaryKind.EachClose:
            return '[[/each]]';
        case LoopBoundaryKind.IfOpen:
            return `[[#if ${expression}]]`;
        case LoopBoundaryKind.IfClose:
            return '[[/if]]';
    }
}

function loopFrameLabelText(labels: FrameLabels, expression: string): string {
    return `${LOOP_REPEAT_ICON} ${labels.repeatsForEach} ${humanizeExpression(expression)}`;
}

function condFrameLabelText(labels: FrameLabels, expression: string): string {
    return `${labels.shownOnlyWhen} ${humanizeExpression(expression)}`;
}

function buildLoopChipSpan(boundary: LoopBoundary): string {
    const label = loopBoundaryLabel(boundary.kind, boundary.expression);
    return `<span class="cke-loop-chip" data-loop-kind="${boundary.kind}" data-loop-expr="${escapeHtml(boundary.expression)}" contenteditable="false">${escapeHtml(label)}</span>`;
}

type TokenSegment =
    | { type: 'text'; value: string }
    | { type: 'loop'; boundary: LoopBoundary }
    | { type: 'scalar'; path: string };

function splitTokenSegments(text: string): TokenSegment[] {
    const blockSegments: TokenSegment[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    BLOCK_MARKER_REGEX.lastIndex = 0;

    while ((match = BLOCK_MARKER_REGEX.exec(text)) !== null) {
        if (match.index > lastIndex) {
            blockSegments.push({ type: 'text', value: text.slice(lastIndex, match.index) });
        }
        blockSegments.push({ type: 'loop', boundary: parseLoopBoundary(match[1]) });
        lastIndex = match.index + match[0].length;
    }
    BLOCK_MARKER_REGEX.lastIndex = 0;
    if (lastIndex < text.length) {
        blockSegments.push({ type: 'text', value: text.slice(lastIndex) });
    }

    const segments: TokenSegment[] = [];
    for (const segment of blockSegments) {
        if (segment.type !== 'text') {
            segments.push(segment);
            continue;
        }
        segments.push(...splitScalarSegments(segment.value));
    }
    return segments;
}

function splitScalarSegments(text: string): TokenSegment[] {
    const segments: TokenSegment[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    PLACEHOLDER_REGEX.lastIndex = 0;

    while ((match = PLACEHOLDER_REGEX.exec(text)) !== null) {
        if (match.index > lastIndex) {
            segments.push({ type: 'text', value: text.slice(lastIndex, match.index) });
        }
        segments.push({ type: 'scalar', path: match[1].trim() });
        lastIndex = match.index + match[0].length;
    }
    PLACEHOLDER_REGEX.lastIndex = 0;
    if (lastIndex < text.length || segments.length === 0) {
        segments.push({ type: 'text', value: text.slice(lastIndex) });
    }
    return segments;
}

interface FrameOpen {
    isLoop: boolean;
    expression: string;
}

function frameOpenMarkup(open: FrameOpen, labels: FrameLabels): string {
    if (open.isLoop) {
        return `<div class="${LOOP_FRAME_CLASS}" data-loop-expr="${escapeHtml(open.expression)}">` +
            `<div class="${LOOP_FRAME_LABEL_CLASS}" contenteditable="false">${escapeHtml(loopFrameLabelText(labels, open.expression))}</div>` +
            `<div class="${LOOP_FRAME_BODY_CLASS}">`;
    }
    return `<div class="${COND_FRAME_CLASS}" data-cond-expr="${escapeHtml(open.expression)}">` +
        `<div class="${COND_FRAME_LABEL_CLASS}" contenteditable="false">${escapeHtml(condFrameLabelText(labels, open.expression))}</div>` +
        `<div class="${COND_FRAME_BODY_CLASS}">`;
}

function frameCloseMarkup(): string {
    return '</div></div>';
}

function wrapBalancedFramesInHtml(html: string, labels: FrameLabels): string {
    let result = '';
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    const openStack: FrameOpen[] = [];
    BLOCK_MARKER_REGEX.lastIndex = 0;

    while ((match = BLOCK_MARKER_REGEX.exec(html)) !== null) {
        const boundary = parseLoopBoundary(match[1]);
        const before = html.slice(lastIndex, match.index);
        const afterMatchIndex = match.index + match[0].length;

        if (boundary.kind === LoopBoundaryKind.EachOpen || boundary.kind === LoopBoundaryKind.IfOpen) {
            const isLoop = boundary.kind === LoopBoundaryKind.EachOpen;
            const closeRange = findBalancedClose(html, afterMatchIndex, boundary.kind);
            if (closeRange === null) {
                result += before + match[0];
                lastIndex = afterMatchIndex;
                continue;
            }
            result += before + frameOpenMarkup({ isLoop, expression: boundary.expression }, labels);
            openStack.push({ isLoop, expression: boundary.expression });
            lastIndex = afterMatchIndex;
            continue;
        }

        const expectsLoop = boundary.kind === LoopBoundaryKind.EachClose;
        const top = openStack[openStack.length - 1];
        if (top && top.isLoop === expectsLoop) {
            result += before + frameCloseMarkup();
            openStack.pop();
            lastIndex = afterMatchIndex;
            continue;
        }

        result += before + match[0];
        lastIndex = afterMatchIndex;
    }
    BLOCK_MARKER_REGEX.lastIndex = 0;

    result += html.slice(lastIndex);
    return result;
}

function findBalancedClose(html: string, fromIndex: number, openKind: LoopBoundaryKind): number | null {
    const scanner = new RegExp(BLOCK_MARKER_REGEX.source, 'g');
    scanner.lastIndex = fromIndex;
    let depth = 1;
    let match: RegExpExecArray | null;
    const expectsLoop = openKind === LoopBoundaryKind.EachOpen;

    while ((match = scanner.exec(html)) !== null) {
        const boundary = parseLoopBoundary(match[1]);
        const sameLoopFamily = boundary.kind === LoopBoundaryKind.EachOpen || boundary.kind === LoopBoundaryKind.IfOpen;
        if (sameLoopFamily) {
            depth++;
            continue;
        }
        const closesLoop = boundary.kind === LoopBoundaryKind.EachClose;
        if (closesLoop === expectsLoop) {
            depth--;
            if (depth === 0) return match.index;
        } else {
            depth--;
            if (depth === 0) return null;
        }
    }
    return null;
}

function frameDivsToTokens(html: string): string {
    const container = document.createElement('div');
    container.innerHTML = html;
    let frame = container.querySelector(`.${LOOP_FRAME_CLASS}, .${COND_FRAME_CLASS}`);
    while (frame) {
        const isLoop = frame.classList.contains(LOOP_FRAME_CLASS);
        const expr = isLoop
            ? frame.getAttribute('data-loop-expr') ?? ''
            : frame.getAttribute('data-cond-expr') ?? '';
        const bodyClass = isLoop ? LOOP_FRAME_BODY_CLASS : COND_FRAME_BODY_CLASS;
        const body = frame.querySelector(`.${bodyClass}`);
        const bodyHtml = body ? body.innerHTML : '';
        const openToken = isLoop ? loopBoundaryToken(LoopBoundaryKind.EachOpen, expr) : loopBoundaryToken(LoopBoundaryKind.IfOpen, expr);
        const closeToken = isLoop ? '[[/each]]' : '[[/if]]';

        const replacement = container.ownerDocument.createElement('template');
        replacement.innerHTML = `${openToken}${bodyHtml}${closeToken}`;
        frame.replaceWith(replacement.content);
        frame = container.querySelector(`.${LOOP_FRAME_CLASS}, .${COND_FRAME_CLASS}`);
    }
    return container.innerHTML;
}

export function createPlaceholderChipPlugin(ck: CK, resolveLabel?: (path: string) => string, frameLabels: FrameLabels = DEFAULT_FRAME_LABELS) {
    return class PlaceholderChipPlugin extends ck.Plugin {
        static get requires() {
            return ['Widget'] as const;
        }

        static get pluginName() {
            return 'PlaceholderChipPlugin' as const;
        }

        init() {
            const editor = this.editor;
            const schema = editor.model.schema;
            const { toWidget, toWidgetEditable } = ck;

            schema.register('placeholder', {
                allowWhere: '$text',
                isInline: true,
                isObject: true,
                allowAttributes: ['path']
            });

            schema.register('loopBoundary', {
                allowWhere: '$text',
                isInline: true,
                isObject: true,
                allowAttributes: ['loopKind', 'loopExpr']
            });

            schema.register('loopFrame', {
                allowWhere: '$block',
                isObject: true,
                allowAttributes: ['loopExpr']
            });
            schema.register('loopFrameBody', {
                allowIn: 'loopFrame',
                allowContentOf: '$root',
                isLimit: true
            });

            schema.register('condFrame', {
                allowWhere: '$block',
                isObject: true,
                allowAttributes: ['condExpr']
            });
            schema.register('condFrameBody', {
                allowIn: 'condFrame',
                allowContentOf: '$root',
                isLimit: true
            });

            schema.register('widgetSnapshot', {
                allowWhere: '$block',
                isObject: true,
                allowAttributes: ['widgetTag', 'widgetAttrs', 'snapshotHtml']
            });

            editor.conversion.for('upcast').add(dispatcher => {
                dispatcher.on('element:div', (evt, data, conversionApi) => {
                    const viewElement = data.viewItem;
                    if (!viewElement.hasClass(WIDGET_SNAPSHOT_CLASS)) return;
                    if (!conversionApi.consumable.consume(viewElement, { name: true })) return;

                    const widgetTag = viewElement.getAttribute('data-widget-tag') ?? '';
                    const widgetAttrs = viewElement.getAttribute('data-widget-attrs') ?? '';
                    const snapshotHtml = decodeSnapshotHtml(viewElement.getAttribute('data-snapshot') ?? '');

                    const snapshotElement = conversionApi.writer.createElement('widgetSnapshot', { widgetTag, widgetAttrs, snapshotHtml });
                    if (!conversionApi.safeInsert(snapshotElement, data.modelCursor)) return;
                    conversionApi.updateConversionResult(snapshotElement, data);
                    evt.stop();
                }, { priority: 'highest' });

                dispatcher.on('element:div', (evt, data, conversionApi) => {
                    const viewElement = data.viewItem;
                    const isLoop = viewElement.hasClass(LOOP_FRAME_CLASS);
                    const isCond = viewElement.hasClass(COND_FRAME_CLASS);
                    if (!isLoop && !isCond) return;
                    if (!conversionApi.consumable.consume(viewElement, { name: true })) return;

                    const frameName = isLoop ? 'loopFrame' : 'condFrame';
                    const exprAttr = isLoop ? 'data-loop-expr' : 'data-cond-expr';
                    const modelAttr = isLoop ? 'loopExpr' : 'condExpr';
                    const bodyName = isLoop ? 'loopFrameBody' : 'condFrameBody';
                    const bodyClass = isLoop ? LOOP_FRAME_BODY_CLASS : COND_FRAME_BODY_CLASS;

                    const expression = viewElement.getAttribute(exprAttr) ?? '';
                    const frameElement = conversionApi.writer.createElement(frameName, { [modelAttr]: expression });
                    if (!conversionApi.safeInsert(frameElement, data.modelCursor)) return;

                    const bodyElement = conversionApi.writer.createElement(bodyName);
                    conversionApi.writer.append(bodyElement, frameElement);

                    const children = Array.from(viewElement.getChildren()) as import('ckeditor5').ViewNode[];
                    const bodyView = children.find(child => child.is('element') && child.hasClass(bodyClass));
                    if (bodyView && bodyView.is('element')) {
                        conversionApi.consumable.consume(bodyView, { name: true });
                        conversionApi.convertChildren(bodyView, conversionApi.writer.createPositionAt(bodyElement, 0));
                    }

                    conversionApi.updateConversionResult(frameElement, data);
                    evt.stop();
                }, { priority: 'highest' });

                dispatcher.on('element:span', (evt, data, conversionApi) => {
                    const viewElement = data.viewItem;
                    if (!viewElement.hasClass('cke-loop-chip')) return;

                    const loopKind = viewElement.getAttribute('data-loop-kind');
                    if (!loopKind) return;
                    const loopExpr = viewElement.getAttribute('data-loop-expr') ?? '';

                    const modelElement = conversionApi.writer.createElement('loopBoundary', { loopKind, loopExpr });
                    if (!conversionApi.safeInsert(modelElement, data.modelCursor)) return;
                    conversionApi.updateConversionResult(modelElement, data);
                    evt.stop();
                }, { priority: 'highest' });

                dispatcher.on('element:span', (evt, data, conversionApi) => {
                    const viewElement = data.viewItem;
                    if (!viewElement.hasClass('cke-placeholder-chip')) return;

                    const path = viewElement.getAttribute('data-path');
                    if (!path) return;

                    const modelElement = conversionApi.writer.createElement('placeholder', { path });
                    if (!conversionApi.safeInsert(modelElement, data.modelCursor)) return;
                    conversionApi.updateConversionResult(modelElement, data);
                    evt.stop();
                }, { priority: 'high' });

                dispatcher.on('text', (evt, data, conversionApi) => {
                    const text = (data.viewItem as { data?: string }).data ?? '';
                    const segments = splitTokenSegments(text);
                    if (segments.length === 1 && segments[0].type === 'text') return;

                    const startPosition = data.modelCursor;
                    let cursor = data.modelCursor;

                    for (const segment of segments) {
                        if (segment.type === 'text') {
                            const textNode = conversionApi.writer.createText(segment.value);
                            conversionApi.writer.insert(textNode, cursor);
                            cursor = cursor.getShiftedBy(segment.value.length);
                            continue;
                        }
                        const modelElement = segment.type === 'loop'
                            ? conversionApi.writer.createElement('loopBoundary', {
                                loopKind: segment.boundary.kind,
                                loopExpr: segment.boundary.expression
                            })
                            : conversionApi.writer.createElement('placeholder', { path: segment.path });
                        if (conversionApi.safeInsert(modelElement, cursor)) {
                            cursor = conversionApi.writer.createPositionAfter(modelElement);
                        }
                    }

                    data.modelCursor = cursor;
                    data.modelRange = conversionApi.writer.createRange(startPosition, cursor);
                    evt.stop();
                }, { priority: 'high' });
            });

            editor.conversion.for('editingDowncast').elementToElement({
                model: 'placeholder',
                view: (modelElement, { writer }) => {
                    const path = modelElement.getAttribute('path') as string;
                    const label = resolveLabel ? resolveLabel(path) : path;
                    const span = writer.createContainerElement('span', {
                        class: 'cke-placeholder-chip',
                        'data-path': path
                    });
                    const inner = writer.createText(`{{ ${label} }}`);
                    writer.insert(writer.createPositionAt(span, 0), inner);
                    return toWidget(span, writer, { label: `${path} placeholder` });
                }
            });

            editor.conversion.for('dataDowncast').elementToElement({
                model: 'placeholder',
                view: (modelElement, { writer }) => {
                    const path = modelElement.getAttribute('path') as string;
                    const span = writer.createContainerElement('span', {
                        class: 'cke-placeholder-chip',
                        'data-path': path
                    });
                    const inner = writer.createText(`[[${path}]]`);
                    writer.insert(writer.createPositionAt(span, 0), inner);
                    return span;
                }
            });

            editor.conversion.for('editingDowncast').elementToElement({
                model: 'loopBoundary',
                view: (modelElement, { writer }) => {
                    const kind = modelElement.getAttribute('loopKind') as LoopBoundaryKind;
                    const expr = (modelElement.getAttribute('loopExpr') as string) ?? '';
                    const span = writer.createContainerElement('span', {
                        class: 'cke-loop-chip',
                        'data-loop-kind': kind,
                        'data-loop-expr': expr
                    });
                    const inner = writer.createText(loopBoundaryLabel(kind, expr));
                    writer.insert(writer.createPositionAt(span, 0), inner);
                    return toWidget(span, writer, { label: loopBoundaryLabel(kind, expr) });
                }
            });

            editor.conversion.for('dataDowncast').elementToElement({
                model: 'loopBoundary',
                view: (modelElement, { writer }) => {
                    const kind = modelElement.getAttribute('loopKind') as LoopBoundaryKind;
                    const expr = (modelElement.getAttribute('loopExpr') as string) ?? '';
                    const span = writer.createContainerElement('span', {
                        class: 'cke-loop-chip',
                        'data-loop-kind': kind,
                        'data-loop-expr': expr
                    });
                    const inner = writer.createText(loopBoundaryToken(kind, expr));
                    writer.insert(writer.createPositionAt(span, 0), inner);
                    return span;
                }
            });

            editor.conversion.for('editingDowncast').elementToElement({
                model: 'loopFrame',
                view: (modelElement, { writer }) => {
                    const expr = (modelElement.getAttribute('loopExpr') as string) ?? '';
                    const container = writer.createContainerElement('div', { class: LOOP_FRAME_CLASS, 'data-loop-expr': expr });
                    const header = writer.createRawElement('div', { class: LOOP_FRAME_LABEL_CLASS }, domElement => {
                        domElement.textContent = loopFrameLabelText(frameLabels, expr);
                    });
                    writer.insert(writer.createPositionAt(container, 0), header);
                    return toWidget(container, writer, { hasSelectionHandle: false });
                }
            });
            editor.conversion.for('editingDowncast').elementToElement({
                model: 'loopFrameBody',
                view: (_modelElement, { writer }) => {
                    const body = writer.createEditableElement('div', { class: LOOP_FRAME_BODY_CLASS });
                    return toWidgetEditable(body, writer);
                }
            });

            editor.conversion.for('editingDowncast').elementToElement({
                model: 'condFrame',
                view: (modelElement, { writer }) => {
                    const expr = (modelElement.getAttribute('condExpr') as string) ?? '';
                    const container = writer.createContainerElement('div', { class: COND_FRAME_CLASS, 'data-cond-expr': expr });
                    const header = writer.createRawElement('div', { class: COND_FRAME_LABEL_CLASS }, domElement => {
                        domElement.textContent = condFrameLabelText(frameLabels, expr);
                    });
                    writer.insert(writer.createPositionAt(container, 0), header);
                    return toWidget(container, writer, { hasSelectionHandle: false });
                }
            });
            editor.conversion.for('editingDowncast').elementToElement({
                model: 'condFrameBody',
                view: (_modelElement, { writer }) => {
                    const body = writer.createEditableElement('div', { class: COND_FRAME_BODY_CLASS });
                    return toWidgetEditable(body, writer);
                }
            });

            editor.conversion.for('dataDowncast').elementToElement({
                model: 'loopFrame',
                view: (modelElement, { writer }) => {
                    const expr = (modelElement.getAttribute('loopExpr') as string) ?? '';
                    return writer.createContainerElement('div', { class: LOOP_FRAME_CLASS, 'data-loop-expr': expr });
                }
            });
            editor.conversion.for('dataDowncast').elementToElement({
                model: 'loopFrameBody',
                view: (_modelElement, { writer }) => writer.createContainerElement('div', { class: LOOP_FRAME_BODY_CLASS })
            });
            editor.conversion.for('dataDowncast').elementToElement({
                model: 'condFrame',
                view: (modelElement, { writer }) => {
                    const expr = (modelElement.getAttribute('condExpr') as string) ?? '';
                    return writer.createContainerElement('div', { class: COND_FRAME_CLASS, 'data-cond-expr': expr });
                }
            });
            editor.conversion.for('dataDowncast').elementToElement({
                model: 'condFrameBody',
                view: (_modelElement, { writer }) => writer.createContainerElement('div', { class: COND_FRAME_BODY_CLASS })
            });

            editor.conversion.for('editingDowncast').elementToElement({
                model: 'widgetSnapshot',
                view: (modelElement, { writer }) => {
                    const widgetTag = (modelElement.getAttribute('widgetTag') as string) ?? '';
                    const snapshotHtml = (modelElement.getAttribute('snapshotHtml') as string) ?? '';
                    const placeholderLabel = getWidgetDisplayLabel(widgetTag);
                    // toWidget must wrap a container element (not a raw element). The snapshot HTML
                    // is injected into a raw CHILD, mirroring how the frame widgets nest their label.
                    const container = writer.createContainerElement('div', {
                        class: WIDGET_SNAPSHOT_CLASS,
                        'data-widget-tag': widgetTag
                    });
                    const label = writer.createRawElement('div', { class: `${WIDGET_SNAPSHOT_CLASS}__label` }, domElement => {
                        domElement.textContent = `${LOOP_REPEAT_ICON} ${placeholderLabel} · template — fills with live data on generate`;
                    });
                    const content = writer.createRawElement(
                        'div',
                        { class: `${WIDGET_SNAPSHOT_CLASS}__content`, contenteditable: 'false' },
                        domElement => {
                            domElement.innerHTML = snapshotHtml.trim()
                                ? snapshotHtml
                                : `<div class="${WIDGET_SNAPSHOT_CLASS}__placeholder">${escapeHtml(placeholderLabel)}</div>`;
                        }
                    );
                    writer.insert(writer.createPositionAt(container, 0), label);
                    writer.insert(writer.createPositionAt(container, 'end'), content);
                    return toWidget(container, writer, { hasSelectionHandle: true, label: placeholderLabel });
                }
            });

            editor.conversion.for('dataDowncast').elementToElement({
                model: 'widgetSnapshot',
                view: (modelElement, { writer }) => {
                    const widgetTag = (modelElement.getAttribute('widgetTag') as string) ?? '';
                    const widgetAttrs = (modelElement.getAttribute('widgetAttrs') as string) ?? '';
                    const parsedAttrs = parseWidgetAttrs(widgetAttrs);
                    return writer.createRawElement(widgetTag, parsedAttrs, () => { });
                }
            });
        }
    };
}

export function getCursorLoopScope(editor: import('ckeditor5').ClassicEditor): string | null {
    // Resolve from the MODEL selection, not the editing-view selection. The listener fires on
    // the model's change:range, at which point the view selection has not synced yet (it lags for
    // programmatic moves), but the view tree has — so mapping the model position forward is current.
    const modelPosition = editor.model.document.selection.getFirstPosition();
    if (!modelPosition) return null;
    const view = editor.editing.view;
    let domParent: Node | null = null;
    try {
        const viewPosition = editor.editing.mapper.toViewPosition(modelPosition);
        const domPos = view.domConverter.viewPositionToDom(viewPosition) as { parent: Node } | null;
        domParent = domPos?.parent ?? null;
    } catch {
        return null;
    }
    let el: HTMLElement | null =
        domParent && domParent.nodeType === Node.TEXT_NODE
            ? domParent.parentElement
            : (domParent as HTMLElement | null);
    while (el) {
        const each = el.getAttribute?.('data-tpl-each');
        if (each) return each;
        el = el.parentElement;
    }
    return null;
}

export function insertPlaceholderAtCursor(editor: import('ckeditor5').ClassicEditor, fieldPath: string): void {
    editor.editing.view.focus();
    editor.model.change(writer => {
        const selection = editor.model.document.selection;
        let position = selection.getFirstPosition();
        if (!position) {
            const root = editor.model.document.getRoot();
            if (!root) return;
            position = writer.createPositionAt(root, 'end');
        }

        const prevNode = position.nodeBefore;
        if (prevNode && prevNode.is('element', 'placeholder')) {
            const spaceText = writer.createText(' ');
            writer.insert(spaceText, position);
            position = position.getShiftedBy(1);
        }

        const chip = writer.createElement('placeholder', { path: fieldPath });
        writer.insert(chip, position);
        writer.setSelection(writer.createPositionAfter(chip));
    });
}

function widgetSnapshotDivsToTags(html: string): string {
    const container = document.createElement('div');
    container.innerHTML = html;
    let snapshot = container.querySelector(`.${WIDGET_SNAPSHOT_CLASS}`);
    while (snapshot) {
        const widgetTag = snapshot.getAttribute('data-widget-tag') ?? '';
        if (!widgetTag) {
            snapshot.remove();
        } else {
            const replacement = container.ownerDocument.createElement(widgetTag);
            const parsedAttrs = parseWidgetAttrs(snapshot.getAttribute('data-widget-attrs') ?? '');
            for (const [name, value] of Object.entries(parsedAttrs)) {
                replacement.setAttribute(name, value);
            }
            snapshot.replaceWith(replacement);
        }
        snapshot = container.querySelector(`.${WIDGET_SNAPSHOT_CLASS}`);
    }
    return container.innerHTML;
}

export function stripChipMarkupForStorage(html: string): string {
    const withSnapshots = html.includes(WIDGET_SNAPSHOT_CLASS)
        ? widgetSnapshotDivsToTags(html)
        : html;
    const withFrames = (withSnapshots.includes(LOOP_FRAME_CLASS) || withSnapshots.includes(COND_FRAME_CLASS))
        ? frameDivsToTokens(withSnapshots)
        : withSnapshots;
    const withLoops = withFrames.replace(
        /<span[^>]*class="cke-loop-chip"[^>]*>[\s\S]*?<\/span>/g,
        match => {
            const kind = match.match(/data-loop-kind="([^"]*)"/)?.[1] as LoopBoundaryKind | undefined;
            if (!kind) return match;
            const expr = decodeHtmlEntities(match.match(/data-loop-expr="([^"]*)"/)?.[1] ?? '');
            return loopBoundaryToken(kind, expr);
        }
    );
    return withLoops.replace(
        /<span[^>]+class="cke-placeholder-chip"[^>]+data-path="([^"]+)"[^>]*>[\s\S]*?<\/span>/g,
        (_, path: string) => `[[${path}]]`
    );
}

function decodeHtmlEntities(value: string): string {
    return value
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
}

export function preprocessHtmlForEditor(
    html: string,
    userInputDefaults?: Record<string, string>,
    snapshotMap?: Map<string, string[]>,
    frameLabels: FrameLabels = DEFAULT_FRAME_LABELS
): string {
    const userInputRegex = /\[\[userInputs\.([a-zA-Z_$][a-zA-Z0-9_$]*)\]\]/g;
    const substituted = userInputDefaults
        ? html.replace(userInputRegex, (match, key: string) =>
            key in userInputDefaults ? userInputDefaults[key] : match)
        : html;

    const withWidgets = preprocessWidgetsForEditor(substituted, snapshotMap);
    const withFrames = wrapBalancedFramesInHtml(withWidgets, frameLabels);

    const container = document.createElement('div');
    container.innerHTML = withFrames;

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
        acceptNode(node: Node): number {
            const parent = node.parentElement;
            if (parent && (parent.classList.contains(LOOP_FRAME_LABEL_CLASS) || parent.classList.contains(COND_FRAME_LABEL_CLASS))) {
                return NodeFilter.FILTER_REJECT;
            }
            return NodeFilter.FILTER_ACCEPT;
        }
    });
    const textNodes: Text[] = [];
    let node = walker.nextNode();
    while (node) {
        textNodes.push(node as Text);
        node = walker.nextNode();
    }

    for (const textNode of textNodes) {
        const value = textNode.nodeValue ?? '';
        if (!value.includes('[[')) continue;

        const segments = splitTokenSegments(value);
        if (segments.length === 1 && segments[0].type === 'text') continue;

        const fragment = document.createDocumentFragment();
        for (const segment of segments) {
            if (segment.type === 'text') {
                fragment.appendChild(document.createTextNode(segment.value));
                continue;
            }
            const span = document.createElement('span');
            span.innerHTML = segment.type === 'loop'
                ? buildLoopChipSpan(segment.boundary)
                : buildChipSpan(segment.path);
            fragment.appendChild(span.firstChild as Node);
        }

        textNode.parentNode?.replaceChild(fragment, textNode);
    }

    return container.innerHTML;
}

// For non-editor consumers (read-only structural previews) the CKEditor downcast that swaps a chip's
// raw path for its friendly label never runs, so do that swap directly on the static HTML.
export function humanizePlaceholderChips(html: string): string {
    if (!html.includes('cke-placeholder-chip')) return html;
    const container = document.createElement('div');
    container.innerHTML = html;
    container.querySelectorAll('.cke-placeholder-chip[data-path]').forEach(el => {
        const path = el.getAttribute('data-path') ?? '';
        el.textContent = `{{ ${humanizeExpression(path)} }}`;
    });
    return container.innerHTML;
}
